(() => {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || typeof drawer.showModal !== 'function') return;

  const content = drawer.querySelector('[data-cart-content]');
  const status = drawer.querySelector('[data-cart-status]');
  const errorBox = drawer.querySelector('[data-cart-error]');
  const closeButton = drawer.querySelector('[data-cart-close]');
  const sectionId = drawer.dataset.sectionId;
  const activeForms = new WeakSet();
  let trigger = null;
  let pending = 0;
  let queue = Promise.resolve();
  const pcStorageKey = 'elite-pc-basket-v1';
  const maxPCQuantity = 10;
  const pcQuantity = pc => Number.isSafeInteger(pc.quantity) && pc.quantity >= 1 && pc.quantity <= maxPCQuantity ? pc.quantity : 1;
  const pcTotal = pc => Math.round(pc.amount * 100) * pcQuantity(pc) / 100;
  const pcRequestIds = new Map();
  // A restored basket is rechecked on demand; saved display data is not proof
  // of compatibility. Successful checks apply only to the exact selection.
  const pcChecks = new Map();
  let pcAddition = null;
  let pcAdditionCheck = null;
  const pcMoney = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const pcSignature = (selection) => JSON.stringify({
    selections: Object.entries(selection.selections).sort(([a], [b]) => a.localeCompare(b)),
    accessories: [...selection.accessories].sort((a, b) => a.component.localeCompare(b.component)),
  });
  const safeUrl = (value, image = false) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' && (url.origin === location.origin || (image && url.hostname === 'cdn.shopify.com')) ? url.href : '';
    } catch (_) { return ''; }
  };
  const cleanPC = (value) => {
    if (!value || typeof value.title !== 'string' || !value.selection?.selections || !Array.isArray(value.selection.accessories)
      || !Array.isArray(value.parts) || !Number.isFinite(value.amount) || value.amount <= 0) return null;
    const entries = Object.entries(value.selection.selections);
    if (!entries.length || entries.length > 20 || entries.some(([key, sku]) => !/^[a-z_]+$/.test(key) || typeof sku !== 'string' || sku.length > 200)
      || value.selection.accessories.length > 15 || value.selection.accessories.some(item => !item || typeof item.component !== 'string' || typeof item.sku !== 'string' || !/^\d+$/.test(String(item.variantId)))) return null;
    return {
      selection: { selections: Object.fromEntries(entries), accessories: value.selection.accessories.map(({ component, sku, variantId }) => ({ component, sku, variantId })) },
      title: value.title.slice(0, 300), image: safeUrl(value.image || '', true), url: safeUrl(value.url || ''),
      parts: value.parts.slice(0, 35).filter(part => typeof part?.label === 'string' && typeof part.value === 'string')
        .map(({ label, value }) => ({ label: label.slice(0, 100), value: value.slice(0, 300) })),
      amount: value.amount,
      quantity: pcQuantity(value),
    };
  };
  const readPCs = () => {
    try { const saved = JSON.parse(localStorage.getItem(pcStorageKey) || '[]'); return Array.isArray(saved) ? saved.map(cleanPC).filter(Boolean).slice(0, 10) : []; }
    catch (_) { return []; }
  };
  let pcs = readPCs();
  const savePCs = () => { try { localStorage.setItem(pcStorageKey, JSON.stringify(pcs)); } catch (_) { /* Keep the current basket usable when storage is unavailable. */ } };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const removeIcon = () => {
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('width', '18'); icon.setAttribute('height', '18'); icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 10v7M14 10v7');
    icon.append(path); return icon;
  };
  const pcControls = pc => {
    const actions = element('div', 'elite-cart-drawer__pc-actions');
    const label = element('span', 'elite-cart-drawer__quantity-label', 'Quantity');
    const quantity = element('div', 'elite-cart-drawer__quantity');
    const input = element('input');
    input.type = 'number'; input.min = '1'; input.max = String(maxPCQuantity); input.step = '1'; input.inputMode = 'numeric';
    input.value = String(pcQuantity(pc)); input.dataset.pcQuantity = ''; input.dataset.currentQuantity = input.value;
    input.setAttribute('aria-label', `Quantity for ${pc.title}`);
    const adjust = (amount, text) => {
      const button = element('button', '', text); button.type = 'button'; button.dataset.pcAdjust = String(amount);
      button.setAttribute('aria-label', `${amount < 0 ? 'Decrease' : 'Increase'} quantity for ${pc.title}`);
      button.dataset.quantityBound = String(amount < 0 ? pcQuantity(pc) <= 1 : pcQuantity(pc) >= maxPCQuantity);
      button.disabled = button.dataset.quantityBound === 'true'; return button;
    };
    quantity.append(adjust(-1, '−'), input, adjust(1, '+'));
    const remove = element('button', 'elite-cart-drawer__remove'); remove.type = 'button'; remove.dataset.pcRemove = '';
    remove.setAttribute('aria-label', `Remove ${pc.title}`); remove.append(removeIcon(), element('span', '', 'Remove'));
    actions.append(label, quantity, remove); return actions;
  };
  const buildCheck = (check = { state: 'unverified' }, adding = false, pc = null) => {
    const states = {
      unverified: ['Build check required', 'Check component compatibility, stock and the latest PC price.'],
      checking: ['Checking your build', 'Confirming compatibility, stock and the full PC price.'],
      compatible: ['Components compatible', 'Stock and PC price confirmed.'],
      incompatible: ['Not compatible', 'Review your selected components.'],
      unavailable: ['A component is unavailable', 'Review the availability of your selected components.'],
      error: ['Build check could not finish', 'Please try the check again.'],
    };
    const state = states[check.state] ? check.state : 'error';
    const panel = element('section', 'elite-cart-drawer__build-check');
    panel.dataset.pcBuildCheck = state;
    panel.setAttribute('aria-label', 'Build check');
    panel.setAttribute('role', 'status');
    const icon = element('span', 'elite-cart-drawer__build-icon', state === 'compatible' ? '✓' : state === 'checking' ? '…' : state === 'unverified' ? '○' : '!');
    icon.setAttribute('aria-hidden', 'true');
    const copy = element('div', 'elite-cart-drawer__build-copy');
    copy.append(element('h3', '', states[state][0]), element('p', '', check.message || states[state][1]));
    if (!['checking', 'compatible'].includes(state) || (adding && state === 'compatible')) {
      const retry = element('button', 'elite-cart-drawer__build-action', adding && state === 'compatible' ? 'Add this PC to basket' : state === 'unverified' ? 'Check build' : 'Check again');
      retry.type = 'button';
      retry.dataset[adding ? 'pcAddRetry' : 'pcRecheck'] = '';
      copy.append(retry);
      if (adding) {
        const back = element('button', 'elite-cart-drawer__build-action', 'Back to configuration');
        back.type = 'button'; back.dataset.cartClose = '';
        copy.append(back);
      }
    }
    if (state === 'incompatible' && pc && window.ElitePCRepairs) {
      const host = element('section');
      const signature = pcSignature(pc.selection);
      copy.append(host);
      // Rendering can replace this panel while a check is in flight. A stale
      // suggestion must never change a different or removed basket build.
      queueMicrotask(() => {
        if (!host.isConnected) return;
        void window.ElitePCRepairs.suggest(host, {
          selection: pc.selection,
          productUrl: pc.url,
          isCurrent: () => host.isConnected && signature === pcSignature(pc.selection)
            && (adding ? pcAddition === pc && pcAdditionCheck?.state === 'incompatible' : pcs.includes(pc) && pcChecks.get(signature)?.state === 'incompatible'),
          onSelect: repair => run(async () => {
            if (signature !== pcSignature(pc.selection) || !(adding ? pcAddition === pc : pcs.includes(pc))) return;
            pcRequestIds.delete(signature); pcChecks.delete(signature);
            pc.selection = repair.selection;
            for (const change of repair.changes) {
              const label = change.label || change.key;
              const part = pc.parts.find(item => item.label === label);
              if (part) part.value = change.option.label;
              else pc.parts.push({ label, value: change.option.label });
              if (change.key === 'case') { pc.title = `${change.option.label} Gaming PC`; pc.image = safeUrl(change.option.images?.[0] || '', true); }
            }
            try {
              const url = new URL(pc.url || '/collections/gaming-pcs', location.origin);
              for (const key of [...url.searchParams.keys()]) if (/^(elite_|component_)/.test(key)) url.searchParams.delete(key);
              url.searchParams.set('pc', 'gaming-pc');
              for (const [key, sku] of Object.entries(pc.selection.selections)) url.searchParams.append(`elite_${key}`, sku);
              for (const item of pc.selection.accessories) url.searchParams.append(`elite_${item.component}`, item.sku);
              pc.url = url.href;
            } catch (_) { /* The basket retains its selected components. */ }
            pc.amount = repair.validation.price.amount;
            savePCs();
            if (!adding && pcQuantity(pc) > 1) {
              setPCCheck(pc, 'checking');
              try {
                const validated = await validatePC(pc.selection, undefined, pcQuantity(pc));
                updatePCPrice(pc, validated);
              } catch (error) {
                updatePCPrice(pc, error.pcValidation);
                setPCCheck(pc, error.pcCheckState || 'error', error.message);
                return;
              }
            }
            setPCCheck(pc, 'compatible');
            if (adding) showAdditionCheck('compatible');
            else renderPCs();
            setStatus('Replacement selected. Your PC is compatible and the full price has been updated.');
          }),
        });
      });
    }
    panel.append(icon, copy);
    return panel;
  };
  const showAdditionCheck = (state, message) => {
    pcAdditionCheck = state ? { state, message } : null;
    renderPCs();
  };

  const pcDetails = (pc, adding = false) => {
    const row = element('div', 'elite-cart-drawer__item');
    const photo = element('a', 'elite-cart-drawer__image');
    photo.href = safeUrl(pc.url) || '/collections/gaming-pcs';
    photo.tabIndex = -1;
    photo.setAttribute('aria-hidden', 'true');
    const imageUrl = safeUrl(pc.image, true);
    if (imageUrl) { const image = element('img'); image.src = imageUrl; image.alt = ''; photo.append(image); }
    const details = element('div', 'elite-cart-drawer__details');
    details.append(element('p', 'elite-cart-drawer__variant', adding ? 'Your current build' : 'Configured gaming PC'));
    const title = element('a', 'elite-cart-drawer__title', pc.title.replace(/\s+Gaming PC$/i, ''));
    title.href = photo.getAttribute('href');
    details.append(title);
    details.append(element('p', 'elite-cart-drawer__unit-price', Number.isFinite(pc.amount) && pc.amount > 0
      ? `${pcMoney.format(pc.amount)}${pcQuantity(pc) > 1 ? ' each' : ''}` : 'Confirming price…'));
    const actions = element('div', 'elite-cart-drawer__item-actions');
    const edit = element('a', 'elite-cart-drawer__edit', 'Edit configuration');
    edit.href = photo.getAttribute('href');
    actions.append(edit);
    details.append(actions);
    row.append(photo, details);
    return row;
  };

  const pcSpecification = (pc) => {
    const specification = element('section', 'elite-cart-drawer__specification');
    specification.setAttribute('aria-label', 'Your PC specification');
    const core = element('dl', 'elite-cart-drawer__core-specs');
    const fields = [
      ['Processor', /^(processor|cpu)$/i],
      ['Graphics', /^(graphics card|graphics|gpu)$/i],
      ['Memory', /^(memory|ram)$/i],
      ['Storage', /^(solid state drive|ssd|sata ssd|nvme ssd)$/i],
    ];
    fields.forEach(([label, matches]) => {
      const parts = pc.parts.filter(part => matches.test(part.label.replace(/\s*\([^)]*\)/g, '').trim()) && part.value && !/^(none|not selected)/i.test(part.value));
      if (!parts.length) return;
      const pair = element('div');
      const values = parts.map(part => {
        const storageType = label === 'Storage' ? part.label.match(/\(([^)]*SSD)\)/i)?.[1] : '';
        return storageType && !part.value.toLowerCase().includes(storageType.split(' ')[0].toLowerCase()) ? `${part.value} ${storageType}` : part.value;
      });
      pair.append(element('dt', '', label), element('dd', '', values.join(' + ')));
      core.append(pair);
    });
    if (core.childElementCount) specification.append(core);
    const options = element('details', 'elite-cart-drawer__options');
    options.append(element('summary', '', 'Full specification'));
    const list = element('dl');
    pc.parts.forEach(part => { const pair = element('div'); pair.append(element('dt', '', part.label), element('dd', '', part.value)); list.append(pair); });
    options.append(list);
    specification.append(options);
    return specification;
  };

  const pcServices = (pc) => {
    const services = element('dl', 'elite-cart-drawer__services');
    const warranty = pc.parts.find(part => /^warranty$/i.test(part.label));
    if (warranty) {
      const cover = element('div');
      cover.append(element('dt', '', 'Warranty'), element('dd', '', warranty.value));
      services.append(cover);
    }
    const delivery = element('div');
    delivery.append(element('dt', '', 'Delivery'), element('dd', '', 'Free UK shipping · 3–5 working days'));
    services.append(delivery);
    return services;
  };

  const renderPCs = () => {
    // A closed drawer has no visible scroll position to preserve. Measuring it
    // during startup forces the storefront's first layout unnecessarily.
    const preserveScroll = drawer.open;
    const scrollTop = preserveScroll ? content.scrollTop : 0;
    const previousRows = new Map([...content.querySelectorAll('[data-pc-index]')].map(card => [card.querySelector('.elite-cart-drawer__title')?.href,
      { scrollTop: preserveScroll ? card.querySelector('.elite-cart-drawer__pc-body')?.scrollTop || 0 : 0, expanded: Boolean(card.querySelector('.elite-cart-drawer__options')?.open) }]));
    content.querySelector('[data-pc-basket]')?.remove();
    content.querySelector('[data-pc-add-check]')?.remove();
    const separateAddition = pcAddition && pcAdditionCheck && !pcs.some(pc => pcSignature(pc.selection) === pcSignature(pcAddition.selection));
    const nativeCount = Number(content.dataset.itemCount) || 0;
    drawer.dataset.pcBasketMode = String(pcs.length > 0 || Boolean(separateAddition));
    drawer.dataset.pcBasketLayout = pcs.length === 1 && !nativeCount && !separateAddition ? 'single' : 'list';
    drawer.querySelector('[data-cart-title-text]').textContent = 'Your cart';
    document.querySelectorAll('[data-cart-count]').forEach(badge => { badge.textContent = String(nativeCount + pcs.reduce((sum, pc) => sum + pcQuantity(pc), 0)); });
    const empty = content.querySelector('.elite-cart-drawer__empty');
    if (empty) empty.hidden = pcs.length > 0 || Boolean(separateAddition);
    content.classList.toggle('elite-cart-drawer__content--pcs', pcs.length > 0 || Boolean(separateAddition));
    const nativeCheckout = content.querySelector('button[name="checkout"] span');
    if (nativeCheckout) nativeCheckout.textContent = pcs.length ? 'Checkout other items' : 'Checkout';
    if (separateAddition) {
      const attempt = element('article', 'elite-cart-drawer__pc elite-cart-drawer__pc-attempt');
      attempt.dataset.pcAddCheck = '';
      attempt.append(pcDetails(pcAddition, true), pcSpecification(pcAddition), buildCheck(pcAdditionCheck, true, pcAddition));
      if (Number.isFinite(pcAddition.amount) && pcAddition.amount > 0) {
        const total = element('div', 'elite-cart-drawer__total');
        total.append(element('span', '', 'Full PC price'), element('strong', '', pcMoney.format(pcAddition.amount)));
        attempt.append(total);
      }
      attempt.append(element('p', 'elite-cart-drawer__variant', pcAdditionCheck.state === 'compatible' ? 'Ready to add. Select Add this PC to basket to continue.'
        : pcAdditionCheck.state === 'checking' ? 'Checking this configuration before adding it.' : 'This configuration has not been added to your basket.'));
      content.prepend(attempt);
    }
    if (!pcs.length) return;
    const panel = element('div', 'elite-cart-drawer__pcs');
    panel.dataset.pcBasket = '';
    if (separateAddition) panel.append(element('h3', 'elite-cart-drawer__basket-label', 'Already in your basket'));
    pcs.forEach((pc, index) => {
      const card = element('article', 'elite-cart-drawer__pc');
      card.dataset.pcIndex = String(index);
      const row = pcDetails(pc);
      const footer = element('div', 'elite-cart-drawer__pc-footer');
      const total = element('div', 'elite-cart-drawer__total');
      total.append(element('span', '', pcQuantity(pc) > 1 ? `Total for ${pcQuantity(pc)} PCs` : 'PC total'), element('strong', '', pcMoney.format(pcTotal(pc))));
      const checkout = element('button', 'elite-cart-drawer__checkout', pcs.length > 1 || nativeCount ? 'Checkout this PC' : 'Continue to checkout');
      checkout.type = 'button'; checkout.dataset.pcCheckout = '';
      const note = element('p', 'elite-cart-drawer__variant', 'UK delivery included');
      footer.append(total, note, checkout);
      if (nativeCount || pcs.length > 1) footer.append(element('p', 'elite-cart-drawer__variant', 'This configured PC is checked out separately from other basket items.'));
      const check = pcChecks.get(pcSignature(pc.selection));
      checkout.dataset.pcBuildBlocked = String(['incompatible', 'unavailable'].includes(check?.state));
      checkout.disabled = checkout.dataset.pcBuildBlocked === 'true';
      const body = element('div', 'elite-cart-drawer__pc-body');
      body.append(row, pcControls(pc), pcSpecification(pc), pcServices(pc), buildCheck(check, false, pc));
      card.append(body, footer);
      panel.append(card);
    });
    if (!nativeCount) { const keepShopping = element('button', 'elite-cart-drawer__continue', document.querySelector('[data-a31-component-trigger]') ? 'Back to PC builder' : 'Continue shopping'); keepShopping.type = 'button'; keepShopping.dataset.cartClose = ''; panel.append(keepShopping); }
    if (separateAddition) content.querySelector('[data-pc-add-check]').after(panel);
    else content.prepend(panel);
    for (const card of panel.querySelectorAll('[data-pc-index]')) {
      const previous = previousRows.get(card.querySelector('.elite-cart-drawer__title')?.href);
      if (!previous) continue;
      card.querySelector('.elite-cart-drawer__options').open = previous.expanded;
      if (preserveScroll) card.querySelector('.elite-cart-drawer__pc-body').scrollTop = previous.scrollTop;
    }
    if (preserveScroll) content.scrollTop = scrollTop;
  };
  const setPCCheck = (pc, state, message) => {
    const signature = pcSignature(pc.selection);
    const check = { state, message, savedAt: Date.now() };
    pcChecks.set(signature, check);
    content.querySelectorAll('[data-pc-index]').forEach(card => {
      const saved = pcs[Number(card.dataset.pcIndex)];
      if (!saved || pcSignature(saved.selection) !== signature) return;
      card.querySelector('[data-pc-build-check]')?.replaceWith(buildCheck(check, false, pc));
      const checkout = card.querySelector('[data-pc-checkout]');
      checkout.dataset.pcBuildBlocked = String(['incompatible', 'unavailable'].includes(state));
      checkout.disabled = pending > 0 || checkout.dataset.pcBuildBlocked === 'true';
    });
  };

  const setStatus = (message) => { status.textContent = message; };
  const setError = (message = '') => {
    errorBox.hidden = !message;
    errorBox.querySelector('[data-cart-error-message]').textContent = message;
  };
  const setBusy = (busy) => {
    content.setAttribute('aria-busy', String(busy));
    content.querySelectorAll('button, input').forEach((control) => { control.disabled = busy || control.dataset.pcBuildBlocked === 'true' || control.dataset.quantityBound === 'true'; });
    drawer.querySelectorAll('[data-pc-add-retry]').forEach(control => { control.disabled = busy; });
    drawer.querySelector('[data-cart-refresh]').disabled = busy;
  };

  // Serialize reads and writes so a slower response cannot replace a newer basket.
  const run = (task) => {
    pending += 1;
    setBusy(true);
    const operation = queue.then(task);
    queue = operation.catch(() => {});
    return operation.finally(() => {
      pending -= 1;
      setBusy(pending > 0);
    });
  };

  const show = (opener) => {
    if (drawer.open) return;
    trigger = opener || document.activeElement;
    drawer.showModal();
    document.documentElement.classList.add('elite-cart-open');
    document.querySelectorAll('[data-cart-open]').forEach((link) => link.setAttribute('aria-expanded', 'true'));
    closeButton.focus({ preventScroll: true });
  };

  drawer.addEventListener('close', () => {
    document.documentElement.classList.remove('elite-cart-open');
    document.querySelectorAll('[data-cart-open]').forEach((link) => link.setAttribute('aria-expanded', 'false'));
    if (trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
  });

  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      return await fetch(url, { ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const render = (html) => {
    if (typeof html !== 'string') throw new Error('Basket content is unavailable.');
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const updated = documentFragment.querySelector('[data-cart-content]');
    if (!updated) throw new Error('Basket content is unavailable.');
    const count = Number(updated.dataset.itemCount);
    if (!Number.isFinite(count)) throw new Error('Basket count is unavailable.');
    content.replaceChildren(...updated.childNodes);
    content.dataset.itemCount = String(count);
    renderPCs();
    setBusy(pending > 0);
    document.dispatchEvent(new CustomEvent('elite:cart-updated', { detail: { itemCount: count + pcs.reduce((sum, pc) => sum + pcQuantity(pc), 0) } }));
  };

  const refreshContent = async () => {
    const url = new URL(drawer.dataset.cartUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    const response = await request(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Could not refresh your basket.');
    render(await response.text());
  };

  const renderResponse = async (data) => {
    if (data.sections?.[sectionId]) {
      try { render(data.sections[sectionId]); return; } catch (_) { /* Fetch the current section below. */ }
    }
    // A section can fail to render after a successful mutation. Never repeat the add.
    await refreshContent();
  };

  const mutate = async (url, body) => {
    const headers = { Accept: 'application/json' };
    if (!(body instanceof FormData)) headers['Content-Type'] = 'application/json';
    const response = await request(url, { method: 'POST', headers, body: body instanceof FormData ? body : JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok || data.status >= 400) {
      const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'This basket change could not be completed.';
      const error = new Error(message);
      error.cartMessage = message;
      throw error;
    }
    return data;
  };

  const recover = async (error, fallback) => {
    setStatus('');
    try {
      await refreshContent();
      setError(error.cartMessage || fallback);
    } catch (_) {
      setError(`${fallback} Refresh your basket before trying again.`);
    }
  };

  const refresh = () => run(async () => {
    setError();
    setStatus('Updating your basket…');
    try { await refreshContent(); setStatus(''); }
    catch (_) { setStatus(''); setError('Your basket could not be loaded. Please refresh it to try again.'); }
  });

  const open = async (opener) => {
    show(opener);
    if (!pending) { pcAddition = null; showAdditionCheck(); }
    const stale = pcs.filter(pc => { const check = pcChecks.get(pcSignature(pc.selection)); return !check || Date.now() - check.savedAt >= 60000; });
    stale.forEach(pc => setPCCheck(pc, 'checking'));
    await refresh();
    for (const pc of stale) if (pcs.includes(pc)) await recheckPC(pc);
  };

  const quotedAmount = (validation) => validation?.success === true && validation.price?.currency === 'GBP'
    && Number.isFinite(validation.price.amount) && validation.price.amount > 0 ? validation.price.amount : null;
  const updatePCPrice = (pc, validation) => {
    const amount = quotedAmount(validation);
    if (amount === null) return false;
    const changed = Math.round(amount * 100) !== Math.round(pc.amount * 100);
    pc.amount = amount;
    const saved = pcs.find(item => pcSignature(item.selection) === pcSignature(pc.selection));
    if (saved) {
      saved.amount = amount;
      if (changed) pcRequestIds.delete(pcSignature(pc.selection));
      savePCs();
    }
    renderPCs();
    return changed;
  };

  const validatePC = async (selection, checkoutRequestId, quantity = 1, freshCheck = false) => {
    const response = await (checkoutRequestId ? request : window.ElitePCQuoteTransport?.fetch || request)('/apps/pc-configurator?action=validate', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...selection, quantity, freshCheck, ...(checkoutRequestId ? { checkout: true, checkoutRequestId } : {}) }),
    });
    const result = await response.json();
    const quantityConfirmed = quantity === 1 || (result.quantity === quantity && Math.round(result.price?.totalAmount * 100) === Math.round(result.price?.amount * 100) * quantity);
    if (!response.ok || result.success === false || !result.compatible || !result.checkoutReady
      || !Number.isFinite(result.price?.amount) || result.price.amount <= 0 || result.price.currency !== 'GBP'
      || !quantityConfirmed) {
      const error = new Error(!quantityConfirmed && result.compatible ? 'The requested quantity could not be confirmed. Refresh your cart and try again.' : result.message || 'Please review this PC’s compatibility and availability before checkout.');
      error.pcCheckState = result.success !== false && result.compatible === false ? 'incompatible' : result.compatible === true && !result.checkoutReady ? 'unavailable' : 'error';
      error.pcValidation = result;
      const reasons = Array.isArray(result.compatibilityReasons) ? result.compatibilityReasons.filter(reason => typeof reason === 'string' && reason.trim()).slice(0, 3) : [];
      if (reasons.length) error.message = [...new Set(reasons)].join(' ');
      throw error;
    }
    return result;
  };

  const addConfiguredPC = (configuration, opener) => {
    show(opener);
    const startCheck = () => {
      pcAddition = configuration;
      setPCCheck(configuration, 'checking');
      showAdditionCheck('checking');
    };
    if (!pending) startCheck();
    return run(async () => {
      if (pcAddition !== configuration) startCheck();
      setError();
      setStatus('');
      try {
        const existingPC = pcs.find(item => pcSignature(item.selection) === pcSignature(configuration.selection));
        const quantity = existingPC ? pcQuantity(existingPC) + 1 : pcQuantity(configuration);
        if (quantity > maxPCQuantity) throw new Error(`You can add up to ${maxPCQuantity} of the same PC.`);
        const result = await validatePC(configuration.selection, undefined, quantity, true);
        updatePCPrice(configuration, result);
        setPCCheck(configuration, 'compatible');
        if (result.checkoutMode === 'cart' && result.cartPayload?.items?.length) {
          const data = await mutate(drawer.dataset.addUrl, { ...result.cartPayload, sections: [sectionId], sections_url: location.pathname });
          showAdditionCheck('compatible');
          try { await renderResponse(data); }
          catch (_) { setStatus(''); setError('Your PC was added, but the basket could not be displayed. Refresh your basket to see it.'); return; }
        } else if (result.checkoutMode === 'draft_order') {
          const pc = cleanPC({ ...configuration, amount: result.price.amount, quantity });
          if (!pc) throw new Error('This PC could not be saved to your basket.');
          const signature = pcSignature(pc.selection);
          const existing = pcs.findIndex(item => pcSignature(item.selection) === signature);
          if (existing >= 0) pcs[existing] = pc;
          else { if (pcs.length >= 10) throw new Error('Please remove a PC from your basket before adding another.'); pcs.push(pc); }
          savePCs();
          renderPCs();
          showAdditionCheck();
        } else throw new Error('The full PC checkout could not be prepared. Please try again.');
        pcAddition = null;
        setStatus('Added to your basket');
        return { added: true, validation: result };
      } catch (error) {
        setStatus('');
        const state = error.pcCheckState || 'error';
        const message = error.message || 'This PC could not be added. Please try again.';
        updatePCPrice(configuration, error.pcValidation);
        setPCCheck(configuration, state, message);
        showAdditionCheck(state, message);
        return { added: false, validation: error.pcValidation };
      }
    });
  };

  const recheckPC = (pc) => run(async () => {
    setError(); setStatus('');
    setPCCheck(pc, 'checking');
    try {
      const result = await validatePC(pc.selection, undefined, pcQuantity(pc), true);
      if (updatePCPrice(pc, result)) {
        setStatus('Your PC price has changed. Review the updated total before checkout.');
      }
      setPCCheck(pc, 'compatible');
    } catch (error) { updatePCPrice(pc, error.pcValidation); setPCCheck(pc, error.pcCheckState || 'error', error.message); }
  });

  const checkoutPC = (pc) => run(async () => {
    setError();
    setStatus('Confirming your PC and full price…');
    setPCCheck(pc, 'checking');
    try {
      const latest = await validatePC(pc.selection, undefined, pcQuantity(pc), true);
      setPCCheck(pc, 'compatible');
      if (updatePCPrice(pc, latest)) {
        setStatus('Your PC price has changed. Review the updated total, then choose Checkout to continue.');
        return;
      }
      const signature = pcSignature(pc.selection);
      let requestId = pcRequestIds.get(signature);
      if (!requestId) {
        requestId = window.crypto?.randomUUID?.() || `pc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        pcRequestIds.set(signature, requestId);
      }
      const result = await validatePC(pc.selection, requestId, pcQuantity(pc));
      if (updatePCPrice(pc, result)) {
        setStatus('Your PC price has changed. Review the updated total, then choose Checkout to continue.');
        return;
      }
      if (result.checkoutMode !== 'draft_order' || !result.checkoutUrl) throw new Error('This PC checkout is unavailable. Please try again.');
      const url = new URL(result.checkoutUrl);
      if (url.protocol !== 'https:' || !['8qg51w-zn.myshopify.com', 'elitepcs.co.uk', 'www.elitepcs.co.uk'].includes(url.hostname)) throw new Error('This PC checkout could not be opened.');
      // A quote and selection may persist; payment URLs and draft credentials never do.
      setStatus('Opening secure checkout…');
      window.location.assign(url.href);
    } catch (error) {
      setStatus('');
      updatePCPrice(pc, error.pcValidation);
      setPCCheck(pc, error.pcCheckState || 'error', error.message || 'Checkout could not be opened. Please try again.');
    }
  });

  const add = async (form, button) => {
    if (activeForms.has(form)) return;
    activeForms.add(form);
    pcAddition = null;
    showAdditionCheck();
    // Build FormData after the existing configurator's submit guards have run.
    const body = new FormData(form);
    body.delete('return_to');
    body.set('sections', sectionId);
    body.set('sections_url', window.location.pathname);
    const label = button?.querySelector('span');
    const originalLabel = label?.textContent;
    const wasDisabled = button?.disabled;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    if (label) label.textContent = 'Adding…';
    show(button);
    try {
      await run(async () => {
        setError();
        setStatus('Adding to your basket…');
        let data;
        try { data = await mutate(drawer.dataset.addUrl, body); }
        catch (error) { await recover(error, 'We could not confirm that this item was added. Please check your basket.'); return; }
        try { await renderResponse(data); setStatus('Added to your basket'); }
        catch (_) { setStatus(''); setError('Your item was added, but the basket could not be displayed. Refresh your basket to see it.'); }
      });
    } finally {
      activeForms.delete(form);
      if (button) { button.disabled = wasDisabled; button.removeAttribute('aria-busy'); }
      if (label) label.textContent = originalLabel;
    }
  };

  const changePCQuantity = async (pc, quantity, control) => {
    if (pending || !pcs.includes(pc)) return;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > maxPCQuantity) {
      if (control?.matches('[data-pc-quantity]')) control.value = String(pcQuantity(pc));
      setError(`Choose a quantity between 1 and ${maxPCQuantity}, or use Remove to take this PC out of your cart.`);
      return;
    }
    if (quantity === pcQuantity(pc)) return;
    const signature = pcSignature(pc.selection);
    const focusSelector = control?.matches('[data-pc-quantity]') ? '[data-pc-quantity]' : `[data-pc-adjust="${control?.dataset.pcAdjust}"]`;
    pc.quantity = quantity; pcRequestIds.delete(signature); pcChecks.delete(signature); savePCs(); renderPCs();
    await recheckPC(pc);
    if (!drawer.open || !pcs.includes(pc)) return;
    const updated = [...content.querySelectorAll('[data-pc-index]')].find(card => pcs[Number(card.dataset.pcIndex)] === pc);
    const focus = updated?.querySelector(focusSelector);
    (focus && !focus.disabled ? focus : updated?.querySelector('[data-pc-quantity]'))?.focus({ preventScroll: true });
  };

  const change = async (line, quantity, control) => {
    if (pending) return;
    const input = line.querySelector('[data-cart-quantity]');
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
      input.value = input.dataset.currentQuantity;
      setError('Please enter a whole number of zero or more.');
      return;
    }
    const key = line.dataset.cartLine;
    const focusSelector = control?.matches('[data-cart-quantity]') ? '[data-cart-quantity]' : control?.matches('[data-cart-adjust]') ? `[data-cart-adjust="${control.dataset.cartAdjust}"]` : '[data-cart-remove]';
    await run(async () => {
      setError();
      setStatus('Updating your basket…');
      let data;
      try {
        data = await mutate(drawer.dataset.changeUrl, { id: key, quantity, sections: [sectionId], sections_url: window.location.pathname });
      } catch (error) { await recover(error, 'We could not confirm this change. Please check your basket.'); return; }
      try { await renderResponse(data); setStatus(quantity === 0 ? 'Item removed from your basket' : 'Basket updated'); }
      catch (_) { setStatus(''); setError('Your basket was updated, but could not be displayed. Refresh your basket to see it.'); }
    });
    if (drawer.open && (!document.activeElement || document.activeElement === document.body || document.activeElement === control)) {
      const updatedLine = Array.from(content.querySelectorAll('[data-cart-line]')).find((item) => item.dataset.cartLine === key);
      (updatedLine?.querySelector(focusSelector) || closeButton).focus({ preventScroll: true });
    }
  };

  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action).pathname)) return;
    // Explicit Buy now and accelerated checkout retain their checkout flow.
    const button = event.submitter || form.querySelector('[name="add"], button[type="submit"]');
    if (button?.name === 'return_to' || button?.matches('[data-product-upsell-buy-now], [name="checkout"]')) return;
    if (form.matches('[data-upsell-add-form]') || button?.disabled) return;
    event.preventDefault();
    void add(form, button);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-cart-open]');
    if (!opener || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    void open(opener);
  });

  drawer.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target === drawer || event.target.closest('[data-cart-close]')) { drawer.close(); return; }
    if (event.target.closest('[data-cart-refresh]')) { void refresh(); return; }
    if (event.target.closest('[data-pc-add-retry]')) { if (!pending && pcAddition) void addConfiguredPC(pcAddition, trigger); return; }
    const pcControl = event.target.closest('[data-pc-remove], [data-pc-checkout], [data-pc-recheck], [data-pc-adjust]');
    if (pcControl) {
      if (pending) return;
      const index = Number(pcControl.closest('[data-pc-index]').dataset.pcIndex);
      const pc = pcs[index];
      if (!pc) return;
      if (pcControl.hasAttribute('data-pc-adjust')) { void changePCQuantity(pc, pcQuantity(pc) + Number(pcControl.dataset.pcAdjust), pcControl); return; }
      if (pcControl.hasAttribute('data-pc-recheck')) { void recheckPC(pc); return; }
      if (pcControl.hasAttribute('data-pc-checkout')) { void checkoutPC(pc); return; }
      pcs.splice(index, 1); pcRequestIds.delete(pcSignature(pc.selection)); pcChecks.delete(pcSignature(pc.selection)); savePCs(); renderPCs();
      setStatus('PC removed from your basket'); closeButton.focus({ preventScroll: true }); return;
    }
    const control = event.target.closest('[data-cart-adjust], [data-cart-remove]');
    if (!control || pending) return;
    const line = control.closest('[data-cart-line]');
    const input = line.querySelector('[data-cart-quantity]');
    const quantity = control.hasAttribute('data-cart-remove') ? 0 : Number(input.dataset.currentQuantity) + Number(control.dataset.cartAdjust);
    void change(line, quantity, control);
  });

  drawer.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-pc-quantity]')) {
      const pc = pcs[Number(input.closest('[data-pc-index]').dataset.pcIndex)];
      if (!pc) return;
      if (!input.value.trim()) { input.value = String(pcQuantity(pc)); return; }
      void changePCQuantity(pc, Number(input.value), input); return;
    }
    if (!input.matches('[data-cart-quantity]')) return;
    if (!input.value.trim()) { input.value = input.dataset.currentQuantity; return; }
    void change(input.closest('[data-cart-line]'), Number(input.value), input);
  });

  drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(drawer.querySelectorAll('a[href]:not([tabindex="-1"]), button:not([disabled]), input:not([disabled]), summary, [tabindex="0"]'))
      .filter((element) => element.getClientRects().length > 0);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.ElitePCS = window.ElitePCS || {};
  renderPCs();
  window.addEventListener('storage', event => { if (event.key === pcStorageKey && !pending) { pcs = readPCs(); renderPCs(); } });
  window.ElitePCS.cartDrawer = { add, open, refresh, addConfiguredPC, close: () => drawer.close() };
})();
